import React, { useMemo } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import rehypeKatex from "rehype-katex";
import "./latex-renderer.css";
import {
  parseLatexSegments,
  renderKatex,
  cleanLatexString,
  normalizeLatexString,
  normalizeMathExpr,
  formatPiecewiseFunction,
  sanitizeMathContent,
  containsLatexPattern,
  parseMatchTheFollowing,
} from "./latex-utils";

export {
  cleanLatexString,
  normalizeLatexString,
  normalizeMathExpr,
  formatPiecewiseFunction,
  sanitizeMathContent,
  containsLatexPattern,
  parseLatexSegments,
  renderKatex,
  parseMatchTheFollowing,
};

/**
 * Standard LaTeX Math Renderer Component
 * Renders inline math ($x$), display math ($$x$$, \[x\]), matrices, fractions, symbols, plain text, and match-the-following tables.
 */
export const LatexRenderer = ({
  text = "",
  className = "",
  style = {},
  inline = false,
  highlight = "",
}) => {
  const safeText = text !== undefined && text !== null ? String(text) : "";

  const matchData = useMemo(() => {
    if (inline || !safeText.trim()) return null;
    return parseMatchTheFollowing(safeText);
  }, [safeText, inline]);

  const segments = useMemo(() => {
    if (matchData || !safeText.trim()) return [];
    return parseLatexSegments(safeText);
  }, [safeText, matchData]);

  if (!safeText.trim()) {
    return null;
  }

  if (matchData) {
    return (
      <div className={`match_following_wrapper ${className}`.trim()} style={style}>
        {matchData.leadIn && (
          <div className="match_following_lead_in">
            <LatexRenderer text={matchData.leadIn} inline highlight={highlight} />
          </div>
        )}
        <div className="match_table_container">
          <div className="match_table_header_row">
            <div className="match_col_header col_left">
              <LatexRenderer text={matchData.col1Header} inline highlight={highlight} />
            </div>
            <div className="match_col_header col_right">
              <LatexRenderer text={matchData.col2Header} inline highlight={highlight} />
            </div>
          </div>
          <div className="match_table_body">
            {matchData.rows.map((row, rIdx) => (
              <div key={rIdx} className="match_table_row">
                <div className="match_cell col_left">
                  {row.col1 ? (
                    <>
                      <span className="match_item_label">{row.col1.label}</span>
                      <span className="match_item_text">
                        <LatexRenderer text={row.col1.text} inline highlight={highlight} />
                      </span>
                    </>
                  ) : null}
                </div>
                <div className="match_cell col_right">
                  {row.col2 ? (
                    <>
                      <span className="match_item_label">{row.col2.label}</span>
                      <span className="match_item_text">
                        <LatexRenderer text={row.col2.text} inline highlight={highlight} />
                      </span>
                    </>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const renderHighlightedString = (content, hl = "", keyPrefix = "") => {
    if (!hl || !hl.trim() || !content) return content;
    try {
      const escaped = hl.trim().replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const regex = new RegExp(`(${escaped})`, "gi");
      const parts = String(content).split(regex);
      if (parts.length <= 1) return content;
      return parts.map((part, pIdx) =>
        regex.test(part) ? (
          <mark key={`${keyPrefix}-hl-${pIdx}`} className="qb_search_highlight">
            {part}
          </mark>
        ) : (
          part
        )
      );
    } catch {
      return content;
    }
  };

  const renderFormattedText = (content, hl = "", baseKey = "fmt") => {
    if (!content) return null;

    const tokens = [];
    const mdRegex = /(\*\*[^*]+\*\*|(?<!\*)\*[^*]+\*(?!\*)|__[^_]+__|(?<!_)_[^_]+_(?!_))/g;
    let lastIndex = 0;
    let match;

    while ((match = mdRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        tokens.push({
          type: "plain",
          text: content.substring(lastIndex, match.index),
        });
      }

      const matchedStr = match[0];
      if (matchedStr.startsWith("**") && matchedStr.endsWith("**")) {
        tokens.push({
          type: "bold",
          text: matchedStr.slice(2, -2),
        });
      } else if (matchedStr.startsWith("__") && matchedStr.endsWith("__")) {
        tokens.push({
          type: "bold",
          text: matchedStr.slice(2, -2),
        });
      } else if (matchedStr.startsWith("*") && matchedStr.endsWith("*")) {
        tokens.push({
          type: "italic",
          text: matchedStr.slice(1, -1),
        });
      } else if (matchedStr.startsWith("_") && matchedStr.endsWith("_")) {
        tokens.push({
          type: "italic",
          text: matchedStr.slice(1, -1),
        });
      }

      lastIndex = mdRegex.lastIndex;
    }

    if (lastIndex < content.length) {
      tokens.push({
        type: "plain",
        text: content.substring(lastIndex),
      });
    }

    return tokens.map((token, tIdx) => {
      const k = `${baseKey}-${tIdx}`;
      const highlighted = renderHighlightedString(token.text, hl, k);
      if (token.type === "bold") {
        return (
          <strong key={k} style={{ fontWeight: 700 }}>
            {highlighted}
          </strong>
        );
      }
      if (token.type === "italic") {
        return <em key={k}>{highlighted}</em>;
      }
      return <React.Fragment key={k}>{highlighted}</React.Fragment>;
    });
  };

  const renderedContent = segments.map((segment, idx) => {
    if (segment.type === "text") {
      const content = segment.content;
      // If it's pure whitespace adjacent to a display math block, omit or collapse
      if (!content.trim()) {
        const isPrevDisplay = idx > 0 && segments[idx - 1].display;
        const isNextDisplay = idx < segments.length - 1 && segments[idx + 1].display;
        if (isPrevDisplay || isNextDisplay) {
          return null;
        }
        return " ";
      }

      // Split text by newlines if it's display mode to preserve line breaks
      if (!inline && content.includes("\n")) {
        const lines = content.split(/\r?\n/).filter((l, i, arr) => l.trim() || (i > 0 && arr[i - 1].trim()));
        return (
          <React.Fragment key={idx}>
            {lines.map((line, lIdx) => (
              <React.Fragment key={lIdx}>
                {lIdx > 0 && <br />}
                {renderFormattedText(line, highlight, `${idx}-${lIdx}`)}
              </React.Fragment>
            ))}
          </React.Fragment>
        );
      }
      return (
        <React.Fragment key={idx}>
          {renderFormattedText(content, highlight, `${idx}`)}
        </React.Fragment>
      );
    }

    // Math segment
    const isDisplay = segment.display && !inline;
    const html = renderKatex(segment.content, { displayMode: isDisplay });

    if (!html || html.includes("katex-error") || html.includes("color: #cc0000") || html.includes("color:#cc0000")) {
      // Clean fallback if KaTeX couldn't parse
      const fallbackText = cleanLatexString(segment.content);
      return (
        <span
          key={idx}
          className="katex_fallback"
          title={segment.content}
        >
          {fallbackText || segment.content}
        </span>
      );
    }

    if (isDisplay) {
      return (
        <div
          key={idx}
          className="katex_rendered_math display_math"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      );
    }

    return (
      <span
        key={idx}
        className="katex_rendered_math"
        dangerouslySetInnerHTML={{ __html: html }}
      />
    );
  });

  const WrapperTag = inline ? "span" : "div";

  return (
    <WrapperTag
      className={`latex-math-container ${inline ? "inline-mode" : "block-mode"} ${className}`.trim()}
      style={style}
    >
      {renderedContent}
    </WrapperTag>
  );
};

/**
 * Markdown Component with integrated LaTeX Math support
 * Automatically preserves and renders math inside Markdown formatting
 */
export const MarkdownWithMath = ({
  children,
  content,
  text,
  className = "",
  style = {},
}) => {
  const markdownText = children || content || text || "";
  const rawText =
    typeof markdownText === "string" ? markdownText : String(markdownText || "");

  if (!rawText.trim()) return null;

  return (
    <div
      className={`markdown-math-content ${className}`.trim()}
      style={style}
    >
      <LatexRenderer text={rawText} />
    </div>
  );
};

export default LatexRenderer;
